'use client'

import { COLLECTION_RAIL_WIDTH } from '@/utils/collection-layout'
import { motion, type MotionStyle } from 'framer-motion'
import useDrawerSpring from '@/utils/hooks/useDrawerSpring'

import CollectionLoadingSkeleton from '@/components/collection-loading-skeleton'
import Link from 'next/link'
import useApiStatus from '@/utils/hooks/useApiStatus'
import { Map as MapIcon } from 'lucide-react'
import React, { FC, ReactElement, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import useCollectionUrlState from '@/utils/hooks/useCollectionUrlState'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useComponentSize from '@/utils/hooks/useComponentSize'
import isFungible from '@/utils/is-fungible'
import CollectionChart from '../tv-collection-chart'
import CollectionFeed from '../collection-feed'
import RemoteCollectionFeed from '../remote-collection-feed'
import event from '@/utils/event'
import ErrorBoundary from '@/components/error-boundary'
import useCollectionAttributes from '@/utils/hooks/useCollectionAttributes'
import { getTapTickerFromSlug } from '@/utils/tap-token'

import { CollectionStats } from '../v2/collection-stats'
import ActivitySocialPanel, { type SidebarTab } from '@/components/collection-social/sidebar-panel'
import { Feed } from '../v2/feed'
import { NatInsights } from '../v2/nat-insights'
import SharedMonke from '@/components/nodemonkes/shared-monke'
import { NatOwners } from '../v2/nat-owners'
import CollectionOffersPanel from '@/components/offers/collection-offers-panel'
import {
	BarChart2,
	History,
	ShoppingCart,
	ChevronLeft,
	ChevronRight,
	Info,
} from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { isCollectionObjectSlug } from '@/utils/collection-object'

const CollectionComponent: FC<{
	metaData: any
	collectionStats: any
	collection_slug: any
	isRemote: boolean
	title: any
	inscriptions: any[]
	isMobile: boolean
	leftPanel?: React.ReactNode
	headerActions?: React.ReactNode
}> = (props): ReactElement => {
	const { data: bitmapStatus } = useApiStatus()
	const bitmapTip = bitmapStatus?.chain_height ?? bitmapStatus?.indexer_height

	const { isMobile, isDesktop, width: viewportWidth } = useWindowSize()
	const { width, ref } = useComponentSize()
	const tapTicker = getTapTickerFromSlug(props.collection_slug)
	const hasTapAlpha = !!tapTicker
	const isTapDmtNatSlug = tapTicker === 'dmt-nat'
	// Mobile opens the Buy view; chart and history remain available in the header.
	const isFungibleSlug = Boolean(isFungible(props.collection_slug) || tapTicker)
	const initialMobileTab = 'feed'
	const [activeTab, setActiveTab] = useCollectionUrlState('tab', isMobile ? initialMobileTab : 'feed', ['feed', 'chart', 'activity'])
	const [_selectionCount, setSelectionCount] = useState(0)

	// Tab switches on mobile preserve page scroll, which makes "Trade now" /
	// back buttons feel broken when the user has scrolled the chart tab. Reset
	// scroll on every tab switch so the new tab opens at the top. The rAF
	// defers the scroll past the radix TabsContent render that would otherwise
	// re-snap scroll position back to where it was.
	const switchTab = useCallback((tab: string) => {
		if (tab !== 'feed' && tab !== 'chart' && tab !== 'activity') return
		setActiveTab(tab)
		if (typeof window !== 'undefined') {
			window.scrollTo({ top: 0 })
			requestAnimationFrame(() => window.scrollTo({ top: 0 }))
		}
	}, [setActiveTab])

	// Snap to top on initial mount. Without this, browser scroll restoration
	// (or a stale Next.js scroll position from the previous route) can land
	// the user at the bottom of the now-shorter chart-first mobile layout.
	useEffect(() => {
		if (typeof window === 'undefined') return
		window.scrollTo({ top: 0 })
		requestAnimationFrame(() => window.scrollTo({ top: 0 }))
	}, [])

	// Mirrors the chart's currently-selected range so the header's change
	// metric flips from "7D" to "1D"/"1M"/etc. in sync with the chart tabs.
	const [chartRange, setChartRange] = useState<{ label: string; value: number } | null>(null)
	const handleChartRangeChange = useCallback((label: string, value: number) => {
		setChartRange((prev) =>
			prev?.label === label && Math.abs(prev.value - value) < 0.000001 ? prev : { label, value }
		)
	}, [])

	const { attributes } = useCollectionAttributes(props.collection_slug)

	const rootRef = useRef<HTMLDivElement>(null)
	const headerRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [containerHeight, setContainerHeight] = useState('100vh')

	useEffect(() => {
		const updateHeight = () => {
			if (!rootRef.current) {
				return
			}

			const viewportHeight = window.innerHeight
			const rootTop = rootRef.current.getBoundingClientRect().top
			const headerHeight = headerRef.current?.getBoundingClientRect().height || 0
			// Fixed footer-v2 is h-8 (32px) pinned to the viewport bottom; reserve
			// that space so content in the feed isn't hidden behind it.
			const footerHeight = 32
			const minimumHeight = isMobile ? 320 : 420
			const nextHeight = Math.max(
				viewportHeight - rootTop - headerHeight - footerHeight,
				minimumHeight
			)
			setContainerHeight(`${nextHeight}px`)
		}

		const resizeObserver = new ResizeObserver(() => updateHeight())

		if (rootRef.current) {
			resizeObserver.observe(rootRef.current)
		}

		if (headerRef.current) {
			resizeObserver.observe(headerRef.current)
		}

		updateHeight()
		window.addEventListener('resize', updateHeight)

		return () => {
			resizeObserver.disconnect()
			window.removeEventListener('resize', updateHeight)
		}
	}, [isMobile])

	const isFungibleCollection = isFungibleSlug
	const isRune = Boolean(props.collection_slug?.startsWith('rune-'))
	const isTapDmtNat = isTapDmtNatSlug
	const isCollectionObject = isCollectionObjectSlug(props.collection_slug)
	const isRemoteCollection = props.isRemote || isCollectionObject
	const showMobileLayout = isMobile

	// Keep token pages chart-first: trading remains visible, while the secondary
	// sales rail starts closed and can be restored from the chart toolbar.
	// The legacy desktop layout begins at 736px, but side rails leave tablet and
	// small-laptop charts unusably narrow. Those viewports start focused on the
	// chart; after mount every panel choice remains under user control.
	const startsChartFocused = !isMobile && !isDesktop
	const [isTradeCollapsed, setIsTradeCollapsed] = useState(startsChartFocused)
	const [isActivityCollapsed, setIsActivityCollapsed] = useState(
		isFungibleSlug || startsChartFocused
	)
	const tradeProgress = useDrawerSpring(!isTradeCollapsed)
	const activityProgress = useDrawerSpring(!isActivityCollapsed)

	// NAT-only: desktop sidebar view switcher between Sales Activity and Owners
	// Which sidebar tab is open — held here so "view owners" can select it.
	// A link to a conversation (`?post=<id>`, e.g. from a notification) lands
	// on the Social tab, which is where that thread opens.
	const [sidebarView, setSidebarView] = useState(() =>
		typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('post')
			? 'social'
			: 'activity'
	)
	// NAT-only: the holders view joins the sidebar's single tab row.
	const natTabs = useMemo<SidebarTab[]>(
		() =>
			tapTicker
				? [
						{
							id: 'owners',
							label: 'Holders',
							render: (height: string) => <NatOwners height={height} hideHeader ticker={tapTicker} />
						}
				  ]
				: [],
		[tapTicker]
	)
	const toggleTradePanel = useCallback(() => {
		if (isTradeCollapsed && !isDesktop) setIsActivityCollapsed(true)
		setIsTradeCollapsed((collapsed) => !collapsed)
	}, [isDesktop, isTradeCollapsed])
	const toggleActivityPanel = useCallback(() => {
		if (isActivityCollapsed && !isDesktop) setIsTradeCollapsed(true)
		setIsActivityCollapsed((collapsed) => !collapsed)
	}, [isActivityCollapsed, isDesktop])
	const handleSelectBulk = React.useCallback(
		(inscription: any) => {
			if (!inscription.escrow) {
				return
			}

			event.emit('add-to-cart', {
				id: inscription.id
			})
			// Buy from the grid with the trade rail closed: the item was landing
			// in a hidden cart. Slide the rail back in (the Feed stays mounted
			// while collapsed, so the selection is already there); on narrow
			// layouts the two rails are exclusive, so the activity rail yields.
			if (isTradeCollapsed) {
				if (!isDesktop) setIsActivityCollapsed(true)
				setIsTradeCollapsed(false)
			}
		},
		[isDesktop, isTradeCollapsed]
	)
	const closeActivityPanel = () => {
		rootRef.current?.querySelector<HTMLButtonElement>('.drawer-edge-activity')?.focus()
		setIsActivityCollapsed(true)
	}
	useEffect(() => {
		if (isMobile || isDesktop || isTradeCollapsed || isActivityCollapsed) return
		const frame = requestAnimationFrame(() => setIsActivityCollapsed(true))
		return () => cancelAnimationFrame(frame)
	}, [isActivityCollapsed, isDesktop, isMobile, isTradeCollapsed])


	if (!viewportWidth) return <CollectionLoadingSkeleton />

	return (
		<div ref={rootRef} className="flex min-h-0 flex-col">
			{props.collection_slug === 'nodemonkes' && <SharedMonke />}
			<div ref={headerRef}>
				{!showMobileLayout && (
					<CollectionStats
						data={props.collectionStats}
						collection={props.metaData}
						slug={props.collection_slug}
						isRemote={isRemoteCollection}
						hideStats={false}
						chartRange={chartRange}
						onChartRangeChange={handleChartRangeChange}
						headerActions={props.headerActions}
					/>
				)}
			</div>
			<div
				ref={containerRef}
				style={isMobile ? undefined : { height: containerHeight, maxHeight: containerHeight }}
				className={isMobile ? 'max-w-[100vw]' : 'flex-1 min-h-0 overflow-hidden max-w-[100vw]'}
			>
				{isMobile ? (
					<>
						<Tabs value={activeTab} onValueChange={switchTab} className="flex flex-col" ref={ref}>
							{!showMobileLayout && (
								<TabsList className="bg-background border-b border-border grid grid-cols-3 h-12 rounded-none shrink-0 p-0">
									<TabsTrigger
										value="feed"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										{props.leftPanel ? (
											<Info size={12} className="mr-1" />
										) : (
											<ShoppingCart size={12} className="mr-1" />
										)}
										<span className="text-[8px]">{props.leftPanel ? 'About' : 'Market'}</span>
									</TabsTrigger>
									<TabsTrigger
										value="chart"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										<BarChart2 size={12} className="mr-1" />
										<span className="text-[8px]">{isTapDmtNat ? 'Insights' : 'Chart'}</span>
									</TabsTrigger>
									<TabsTrigger
										value="activity"
										className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:ring-0 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground"
									>
										<History size={12} className="mr-1" />
										<span className="text-[8px]">Activity</span>
									</TabsTrigger>
								</TabsList>
							)}
							<TabsContent
								value="feed"
                                forceMount
								className={`data-[state=inactive]:hidden w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								{showMobileLayout && (
									<div className="flex items-center gap-2 border-b p-3">

										<CollectionIcon
											className="size-7 shrink-0 overflow-hidden rounded-full"
											collection={props.metaData}
										/>
										<h2 className="flex min-w-0 flex-1 items-center gap-1 truncate text-base font-semibold text-foreground">
											<span className="min-w-0"><span className="block truncate">{props.metaData?.name}</span>{props.collection_slug === 'bitmap' && typeof bitmapTip === 'number' && <span className="block text-[10px] font-mono font-normal text-muted-foreground">Tip #{bitmapTip.toLocaleString('en-US')}</span>}</span>
											<VerifiedBadge slug={props.collection_slug} />
										</h2>
										{/* Icon-only quick access to the chart and sales activity
									    views (the mobile layout has no top tab bar). 16px gap,
									    square rounded hover target, theme-aware in light/dark. */}
										<div className="flex shrink-0 items-center gap-4">
											{props.headerActions && (
												<div className="flex shrink-0 items-center">{props.headerActions}</div>
											)}
{props.collection_slug === 'bitmap' && <Link href="/collection/bitmap/game" aria-label="View map" className="flex size-8 items-center justify-center rounded-md text-foreground/70 hover:bg-foreground/10"><MapIcon size={18} /></Link>}
											<button
												type="button"
												onClick={() => switchTab('chart')}
												aria-label="View chart"
												className="flex size-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
											>
												<BarChart2 size={18} />
											</button>
											<button
												type="button"
												onClick={() => switchTab('activity')}
												aria-label="View activity"
												className="flex size-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
											>
												<History size={18} />
											</button>
										</div>
									</div>
								)}
                                {showMobileLayout && !isFungibleCollection && <div className="px-3 py-3"><CollectionOffersPanel actionsOnly stackActions showBidRebate collection={props.metaData} slug={props.collection_slug} name={props.metaData?.name} /></div>}
								<ErrorBoundary>
									{props.leftPanel || (
										<Feed
											height={containerHeight}
											title={props.title}
											items={props.inscriptions}
											slug={props.collection_slug}
											supply={props.metaData?.total_supply}
											listed={props.collectionStats?.listed}
											meta={props.metaData}
											isMobile={props.isMobile}
											attributes={attributes}
											onSelectBulk={handleSelectBulk}
											bulkSelected={{}}
											isFungible={isFungibleCollection as any}
											isRune={isRune}
											onSelectionCountChange={setSelectionCount}
										/>
									)}
								</ErrorBoundary>
							</TabsContent>
							<TabsContent
								value="chart"
								className={`w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								<ErrorBoundary>
									{isTapDmtNat ? (
										<NatInsights
                                            headerLeading={showMobileLayout ? <button type="button" onClick={() => switchTab('feed')} aria-label="Back to Buy" className="shrink-0 rounded p-1 text-foreground/80 hover:bg-foreground/5"><ChevronLeft size={20} /></button> : undefined}
											collection={props.metaData}
											data={props.collectionStats}
											slug={props.collection_slug}
											chartMaxWidth={10000}
											onRangeChange={handleChartRangeChange}
											onViewActivity={showMobileLayout ? () => switchTab('activity') : undefined}
											onViewOwners={
												showMobileLayout
													? () => {
															setSidebarView('owners')
															switchTab('activity')
													  }
													: undefined
											}
										/>
									) : showMobileLayout ? (
										<CollectionStats
											data={props.collectionStats}
											collection={props.metaData}
											slug={props.collection_slug}
											isRemote={isRemoteCollection}
											hideStats={false}
											chartRange={chartRange}
											onChartRangeChange={handleChartRangeChange}
											mode="insights"
                                            headerLeading={showMobileLayout ? <button type="button" onClick={() => switchTab('feed')} aria-label="Back to Buy" className="shrink-0 rounded p-1 text-foreground/80 hover:bg-foreground/5"><ChevronLeft size={20} /></button> : undefined}
											chartMaxWidth={10000}
											onViewActivity={() => switchTab('activity')}
											headerActions={props.headerActions}
										/>
									) : (
										<CollectionChart
											slug={props.collection_slug}
											collection={props.metaData}
											width={width}
											height={parseInt(containerHeight)}
											minimal
											showPrice
											onRangeChange={handleChartRangeChange}
										/>
									)}
								</ErrorBoundary>
							</TabsContent>
							<TabsContent
								value="activity"
								className={`w-full ${showMobileLayout ? 'mt-0 pb-24' : ''}`}
							>
								{showMobileLayout && (
									<div className="flex items-center gap-2 border-b p-3">
										<button
											type="button"
											onClick={() => switchTab('feed')}
											className="shrink-0 rounded p-1 text-foreground/80 hover:bg-white/5"
											aria-label="Back"
										>
											<ChevronLeft size={20} />
										</button>
										<CollectionIcon
											className="size-7 shrink-0 overflow-hidden rounded-full"
											collection={props.metaData}
										/>
										<h2 className="flex min-w-0 items-center gap-1 truncate text-base font-semibold text-foreground">
											<span className="min-w-0"><span className="block truncate">{props.metaData?.name}</span>{props.collection_slug === 'bitmap' && typeof bitmapTip === 'number' && <span className="block text-[10px] font-mono font-normal text-muted-foreground">Tip #{bitmapTip.toLocaleString('en-US')}</span>}</span>
											<VerifiedBadge slug={props.collection_slug} />
										</h2>
										{props.headerActions && (
											<div className="ml-auto flex shrink-0 items-center">
												{props.headerActions}
											</div>
										)}
									</div>
								)}
								{!isFungibleCollection && <CollectionOffersPanel defaultCollapsed collection={{...props.metaData, slug: props.collection_slug}} slug={props.collection_slug} name={props.metaData?.name} floorSats={props.collectionStats?.floor_price ? Number(props.collectionStats.floor_price) : null} />}
								<div id="nat-sidebar-content">
									<ErrorBoundary>
																			<ActivitySocialPanel
											slug={props.collection_slug}
											name={props.metaData?.name}
											height={containerHeight}
											hideHeader={showMobileLayout || hasTapAlpha}
											extraTabs={natTabs}
											view={sidebarView}
											onViewChange={setSidebarView}
										/>
									</ErrorBoundary>
								</div>
							</TabsContent>
						</Tabs>
						{showMobileLayout && activeTab !== 'feed' && (
							<div className="fixed bottom-8 left-0 right-0 z-40 border-t bg-background px-4 py-3">
								<button
									type="button"
									onClick={() => switchTab('feed')}
									className="h-11 w-full rounded-full bg-[#6CE9A6] text-sm font-semibold text-black hover:bg-[#7FECB2] active:bg-[#5DD999]"
								>
									Trade now
								</button>
							</div>
						)}
					</>
				) : (
					<motion.div
						className="collection-drawers relative grid h-full overflow-hidden border-t"
						style={{ '--rail-width': COLLECTION_RAIL_WIDTH, '--trade-progress': tradeProgress, '--activity-progress': activityProgress } as MotionStyle}
					>
						{[
							{ side: 'trade', collapsed: isTradeCollapsed, toggle: toggleTradePanel },
							{ side: 'activity', collapsed: isActivityCollapsed, toggle: toggleActivityPanel }
						].map(({ side, collapsed, toggle }) => (
							<button key={side} type="button" onClick={toggle}
								aria-controls={`collection-${side}-panel`} aria-expanded={!collapsed}
								aria-label={`${collapsed ? 'Show' : 'Hide'} ${side} panel`}
								title={`${collapsed ? 'Show' : 'Hide'} ${side} panel`}
								className={`drawer-edge-${side} absolute top-16 z-20 flex h-12 w-4 items-center justify-center border border-border bg-muted text-muted-foreground shadow-sm hover:bg-accent hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${side === 'trade' ? 'rounded-r-md border-l-0' : 'rounded-l-md border-r-0'}`}>
								{(side === 'trade') === collapsed ? <ChevronRight className="size-3" /> : <ChevronLeft className="size-3" />}
							</button>
						))}
						<div
							id="collection-trade-panel"
							aria-hidden={isTradeCollapsed}
							inert={isTradeCollapsed}
							className="collection-drawer collection-drawer-trade"
						>
							<div className="drawer-content overflow-y-auto border-r border-border">
								<ErrorBoundary>
									{props.leftPanel || (
										<Feed
											height={containerHeight}
											title={props.title}
											items={props.inscriptions}
											slug={props.collection_slug}
											supply={props.metaData?.total_supply}
											listed={props.collectionStats?.listed}
											meta={props.metaData}
											isMobile={props.isMobile}
											attributes={attributes}
											onSelectBulk={handleSelectBulk}
											bulkSelected={{}}
											isFungible={isFungibleCollection as any}
											isRune={isRune}
											onSelectionCountChange={setSelectionCount}
										/>
									)}
								</ErrorBoundary>
							</div>
						</div>
						<div
							className={`relative min-w-0 w-full ${
								isFungibleCollection ? 'overflow-hidden' : 'overflow-y-auto px-4'
							} ${isMobile && activeTab !== 'chart' ? 'hidden' : ''}`}
							style={isMobile ? undefined : { height: containerHeight }}
							ref={ref}
							id="collection-feed"
						>
							<ErrorBoundary>
								{isFungibleCollection ? (
									<CollectionChart
										slug={props.collection_slug}
										collection={props.metaData}
										width={width}
										height={parseInt(containerHeight)}
										minimal
										showPrice
										showIdentity={false}
										headerActions={props.headerActions}
										onRangeChange={handleChartRangeChange}
									/>
								) : (
									<>
										{isRemoteCollection && (
											<RemoteCollectionFeed
												title={props.title}
												items={props.inscriptions}
												slug={props.collection_slug}
												supply={props.metaData?.total_supply}
												listed={props.collectionStats?.listed}
												meta={props.metaData}
												isMobile={props.isMobile}
												attributes={attributes}
												onSelectBulk={handleSelectBulk}
												bulkSelected={{}}
												disablePreview={isActivityCollapsed}
											/>
										)}
										{!isRemoteCollection && (
											<CollectionFeed
												title={props.title}
												items={props.inscriptions}
												slug={props.collection_slug}
												supply={props.metaData?.total_supply}
												listed={props.collectionStats?.listed}
												meta={props.metaData}
												isMobile={props.isMobile}
												attributes={attributes}
												onSelectBulk={handleSelectBulk}
												bulkSelected={{}}
												disablePreview={isActivityCollapsed}
											/>
										)}
									</>
								)}
							</ErrorBoundary>
						</div>
						{/* Secondary market activity panel; edge tabs control visibility. */}
						<div
							id="collection-activity-panel"
							onKeyDown={event => { if (event.key === 'Escape') closeActivityPanel() }}
							aria-hidden={isActivityCollapsed}
							inert={isActivityCollapsed}
							className="collection-drawer h-full bg-chrome-muted dark:bg-transparent"
						>
							<div className="drawer-content flex flex-col border-l border-border">
								{!isFungibleCollection && <CollectionOffersPanel collection={{...props.metaData, slug: props.collection_slug}} slug={props.collection_slug} name={props.metaData?.name} floorSats={props.collectionStats?.floor_price ? Number(props.collectionStats.floor_price) : null} compact />}
								<div id="nat-sidebar-content" className="flex-1 overflow-hidden">
									<ErrorBoundary>
																			<ActivitySocialPanel
											slug={props.collection_slug}
											name={props.metaData?.name}
											height={containerHeight}
											hideHeader={!isFungibleCollection || hasTapAlpha}
											onCollapse={!isFungibleCollection ? closeActivityPanel : undefined}
											extraTabs={natTabs}
											view={sidebarView}
											onViewChange={setSidebarView}
										/>
									</ErrorBoundary>
								</div>
							</div>
						</div>
					</motion.div>
				)}
			</div>
		</div>
	)
}

export default CollectionComponent
